/**
 * Bouton d’interface.
 *
 * Cible tactile d’au moins 44 × 44 px (recommandation WCAG 2.5.5) : la règle est portée par la
 * classe `.bouton` du thème, pas par chaque écran, pour qu’aucun bouton ne puisse y échapper.
 * L’état de chargement est exposé via `aria-busy` et remplace le libellé par sa variante
 * « en cours » — traduite, comme tout le reste.
 */
import type { ButtonHTMLAttributes, ReactNode } from 'react';

export type VarianteBouton = 'principal' | 'secondaire' | 'discret';

export interface ProprietesBouton extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variante?: VarianteBouton;
  /** Affiche un indicateur d’activité et désactive le bouton. */
  readonly enCours?: boolean;
  /** Libellé affiché pendant l’activité (`ui:auth.connexion.enCours`, …). */
  readonly texteEnCours?: string;
  readonly iconeAvant?: ReactNode;
}

export function Bouton({
  variante = 'principal',
  enCours = false,
  texteEnCours,
  iconeAvant,
  children,
  className,
  disabled,
  type = 'button',
  ...reste
}: ProprietesBouton) {
  const classes = ['bouton', `bouton--${variante}`, className].filter(Boolean).join(' ');
  const libelle = enCours && texteEnCours !== undefined ? texteEnCours : children;
  return (
    <button
      {...reste}
      type={type}
      className={classes}
      disabled={disabled === true || enCours}
      aria-busy={enCours ? true : undefined}
    >
      {enCours ? <span className="tourniquet" aria-hidden="true" /> : iconeAvant}
      <span className="bouton__libelle">{libelle}</span>
    </button>
  );
}
